import {
  createContext,
  useContext,
  useId,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

interface TabsContextValue {
  activeValue: string;
  setActiveValue: (value: string) => void;
  idBase: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error(`<Tabs.${component}> must be rendered inside <Tabs>`);
  }
  return ctx;
}

interface TabsProps {
  children: ReactNode;
  /** Initial active tab value (uncontrolled). */
  defaultValue: string;
  /** Controlled active tab value. When provided, the parent owns the state. */
  value?: string;
  /** Fires whenever the active tab changes (click or keyboard). */
  onValueChange?: (value: string) => void;
  className?: string;
}

/**
 * Accessible tabs (WAI-ARIA APG "Tabs" pattern, automatic activation).
 * Compound API: `<Tabs><Tabs.List><Tabs.Trigger/></Tabs.List><Tabs.Content/></Tabs>`.
 * Controllable via `value`/`onValueChange`; otherwise self-managed from `defaultValue`.
 */
export function Tabs({ children, defaultValue, value, onValueChange, className = '' }: TabsProps) {
  const [internal, setInternal] = useState(defaultValue);
  const idBase = useId();
  const activeValue = value !== undefined ? value : internal;
  const setActiveValue = (next: string) => {
    if (value === undefined) setInternal(next);
    onValueChange?.(next);
  };
  return (
    <TabsContext.Provider value={{ activeValue, setActiveValue, idBase }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  children: ReactNode;
  /** Required: names the tab set for assistive technology. */
  'aria-label': string;
  className?: string;
}

Tabs.List = function TabsList({ children, 'aria-label': ariaLabel, className = '' }: TabsListProps) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`flex gap-1 overflow-x-auto border-b border-gray-200 ${className}`}
    >
      {children}
    </div>
  );
};

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  disabled?: boolean;
  /** Optional count rendered as a pill after the label. */
  count?: number;
  className?: string;
}

Tabs.Trigger = function TabsTrigger({ value, children, disabled = false, count, className = '' }: TabsTriggerProps) {
  const { activeValue, setActiveValue, idBase } = useTabsContext('Trigger');
  const isActive = activeValue === value;

  // Roving focus / automatic activation: Arrow/Home/End move between enabled tabs.
  // The handler lives on the (focusable) tab button — keydown bubbles here from the
  // focused tab — so the tablist container needn't be focusable.
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    const list = event.currentTarget.closest('[role="tablist"]');
    if (!list) return;
    // Enabled tabs in DOM order (filter rather than :disabled selector for jsdom robustness).
    const tabs = Array.from(
      list.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
    ).filter((t) => !t.disabled);
    if (tabs.length === 0) return;
    event.preventDefault();
    const current = tabs.findIndex((t) => t === event.currentTarget);
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
        next = current < 0 ? 0 : (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = current < 0 ? tabs.length - 1 : (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    const target = tabs[next];
    target.focus();
    const targetValue = target.dataset.value;
    if (targetValue !== undefined) setActiveValue(targetValue);
  };

  return (
    <button
      type="button"
      role="tab"
      id={`${idBase}-tab-${value}`}
      data-value={value}
      aria-selected={isActive}
      aria-controls={`${idBase}-panel-${value}`}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      onClick={() => setActiveValue(value)}
      onKeyDown={handleKeyDown}
      className={`min-h-11 shrink-0 whitespace-nowrap border-b-2 px-4 text-sm font-medium
        transition-colors duration-150 motion-reduce:transition-none
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset
        ${isActive ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'}
        ${disabled ? 'cursor-not-allowed opacity-50 hover:border-transparent hover:text-gray-600' : ''}
        ${className}`}
    >
      {children}
      {count !== undefined && (
        <span
          data-testid="tab-count"
          className="ml-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-indigo-50 px-1.5 text-[11px] text-indigo-700"
        >
          {count}
        </span>
      )}
    </button>
  );
};

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
  /**
   * Mount this panel's children even while it is not the active tab. Default `false`.
   *
   * <b>Opt in only when the tab's LABEL depends on data only the panel can fetch</b> — a live count
   * on the trigger being the case this exists for. Tabs hide most of a page, so a queue behind an
   * unclicked tab is invisible; a count on the trigger is what stops it accumulating unseen. But a
   * count cannot be reported by a panel that was never mounted, so mount-on-active and a live count
   * are mutually exclusive and the caller has to choose.
   *
   * <b>The cost is real and is the caller's to justify:</b> every kept-mounted panel fetches and
   * polls whether or not anyone is looking at it. Do not reach for this to make tab-switching feel
   * faster — that trades a permanent, per-interval cost for a one-off one, on every viewer.
   *
   * The panel stays `hidden` when inactive either way, so assistive technology and sequential focus
   * skip it exactly as before. This changes what is MOUNTED, never what is exposed.
   */
  keepMounted?: boolean;
}

Tabs.Content = function TabsContent({
  value,
  children,
  className = '',
  keepMounted = false,
}: TabsContentProps) {
  const { activeValue, idBase } = useTabsContext('Content');
  const isActive = activeValue === value;
  return (
    <div
      role="tabpanel"
      id={`${idBase}-panel-${value}`}
      aria-labelledby={`${idBase}-tab-${value}`}
      hidden={!isActive}
      tabIndex={0}
      className={isActive ? `pt-4 focus-visible:outline-none ${className}` : ''}
    >
      {/* Mount-on-active by default: panel content (and any data fetch it triggers) only renders
          when selected. `keepMounted` opts out for the live-count case documented above. `hidden`
          on the wrapper is unaffected, so the a11y contract is identical in both modes. */}
      {isActive || keepMounted ? children : null}
    </div>
  );
};
